import { Client, Account, Storage, ID, Query } from 'appwrite';

// Appwrite configuration
const client = new Client()
  .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT || 'https://appwrite.beetletz.online/v1')
  .setProject(process.env.REACT_APP_APPWRITE_PROJECT_ID);

const account = new Account(client);
const storage = new Storage(client);

const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID || '691e06cc001f0a51d654';

// Table IDs
const USERS_TABLE_ID = '691f68bd0002e1f35a54';
const STORE_TABLE_ID = '691f73c6000bfc037aa8';
const PAYMENT_METHOD_TABLE_ID = '691f73970021791b3cdf';
const PRODUCTS_TABLE_ID = '691f6a6c001ae183e138';
const ORDERS_TABLE_ID = '691f6c47003878772771';
const CART_TABLE_ID = '691f71c9002ffd69ef22';
const CHART_ROOM_TABLE_ID = '69203d550021dde71e6e';
const CONVERSATIONS_TABLE_ID = 'conversations';
const CONVERSATION_PARTICIPANTS_TABLE_ID = 'conversation_participants';
const MESSAGES_TABLE_ID = 'messages';
const DRIVERS_TABLE_ID = '696228070024ba3b40c4';
const REQUESTS_TABLE_ID = '69622a420020f484be68';
const VEHICLES_TABLE_ID = '69624f8a001984743927';
const REGISTRATION_STAGE_TABLE_ID = '696251e70039388097da';
const ADMIN_ACTIVITY_LOG_TABLE_ID = 'admin_activity_log';
const VEHICLE_TYPES_TABLE_ID = 'vehicleTypes';
const DELIVERY_SETTINGS_TABLE_ID = 'deliverySettings';
const DELIVERY_RATINGS_TABLE_ID = 'deliveryRatings';
const PROMOTIONS_TABLE_ID = 'promotions';
const STORE_APPLICATIONS_TABLE_ID = 'storeApplications';

// Optional API key: when set, TablesDB requests use server auth and bypass user-level permissions.
// Use for admin operations when tables restrict read by customer/store. Keep secret; avoid exposing in production.
const API_KEY = process.env.REACT_APP_APPWRITE_API_KEY;
const hasApiKeyAuth = () => !!API_KEY;

const getTablesDBHeaders = async (requireSession = true) => {
  const headers = { 'Content-Type': 'application/json' };
  const projectId = process.env.REACT_APP_APPWRITE_PROJECT_ID;
  if (projectId) headers['X-Appwrite-Project'] = projectId;

  if (API_KEY) {
    headers['X-Appwrite-Key'] = API_KEY;
    return headers;
  }

  if (requireSession) {
    try {
      await account.get();
      const sessions = await account.listSessions();
      if (!sessions?.sessions?.length) throw new Error('No active session');
    } catch (error) {
      console.error('TablesDB session verification failed:', error);
      throw new Error('Authentication required. Please log in to continue.');
    }
  }

  // When the app runs on a different site than Appwrite (e.g. localhost), the browser
  // blocks Appwrite's session cookie. The SDK keeps a copy in localStorage and sends it
  // as X-Fallback-Cookies; do the same so these raw fetches carry the session too.
  try {
    const fallback = window.localStorage.getItem('cookieFallback');
    if (fallback) headers['X-Fallback-Cookies'] = fallback;
  } catch {
    /* storage unavailable — rely on the cookie */
  }
  return headers;
};

const getTablesDBCredentials = () => (hasApiKeyAuth() ? 'omit' : 'include');

const getTablesDBBaseUrl = () => {
  const endpoint = process.env.REACT_APP_APPWRITE_ENDPOINT || 'https://appwrite.beetletz.online/v1';
  return `${endpoint}/tablesdb/${DATABASE_ID}`;
};

const tablesDB = {
  createRow: async (databaseId, tableId, rowId, data, permissions = null, requireSession = true) => {
    const url = `${getTablesDBBaseUrl()}/tables/${tableId}/rows`;
    const headers = await getTablesDBHeaders(requireSession);
    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: getTablesDBCredentials(),
      mode: 'cors',
      body: JSON.stringify({ rowId, data }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || `Failed to create row (${response.status})`);
    }
    return response.json();
  },

  getRow: async (databaseId, tableId, rowId) => {
    const url = `${getTablesDBBaseUrl()}/tables/${tableId}/rows/${rowId}`;
    const headers = await getTablesDBHeaders();
    const response = await fetch(url, { method: 'GET', headers, credentials: getTablesDBCredentials() });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || `Failed to get row (${response.status})`);
    }
    return response.json();
  },

  listRows: async (databaseId, tableId, queries = []) => {
    const queryStrings = queries.map((q) =>
      typeof q === 'string' ? q : q?.toString?.() ?? String(q)
    );
    const queryParams = queryStrings.length
      ? '?' + queryStrings.map((q) => `queries[]=${encodeURIComponent(q)}`).join('&')
      : '';
    const url = `${getTablesDBBaseUrl()}/tables/${tableId}/rows${queryParams}`;
    const headers = await getTablesDBHeaders();
    const response = await fetch(url, { method: 'GET', headers, credentials: getTablesDBCredentials() });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || `Failed to list rows (${response.status})`);
    }
    return response.json();
  },

  updateRow: async (databaseId, tableId, rowId, data) => {
    const url = `${getTablesDBBaseUrl()}/tables/${tableId}/rows/${rowId}`;
    const headers = await getTablesDBHeaders();
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      credentials: getTablesDBCredentials(),
      body: JSON.stringify({ data }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || `Failed to update row (${response.status})`);
    }
    return response.json();
  },

  deleteRow: async (databaseId, tableId, rowId) => {
    const url = `${getTablesDBBaseUrl()}/tables/${tableId}/rows/${rowId}`;
    const headers = await getTablesDBHeaders();
    const response = await fetch(url, { method: 'DELETE', headers, credentials: getTablesDBCredentials() });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || `Failed to delete row (${response.status})`);
    }
    return response.json();
  },
};

const getFileViewUrl = (fileId) => {
  const bucketId = process.env.REACT_APP_APPWRITE_STORAGE_BUCKET_ID;
  const projectId = process.env.REACT_APP_APPWRITE_PROJECT_ID;
  const endpoint = process.env.REACT_APP_APPWRITE_ENDPOINT || 'https://appwrite.beetletz.online/v1';
  return `${endpoint}/storage/buckets/${bucketId}/files/${fileId}/view?project=${projectId}`;
};

export {
  client,
  account,
  storage,
  tablesDB,
  getFileViewUrl,
  DATABASE_ID,
  Query,
  ID,
  USERS_TABLE_ID,
  STORE_TABLE_ID,
  PAYMENT_METHOD_TABLE_ID,
  PRODUCTS_TABLE_ID,
  ORDERS_TABLE_ID,
  CART_TABLE_ID,
  CHART_ROOM_TABLE_ID,
  CONVERSATIONS_TABLE_ID,
  CONVERSATION_PARTICIPANTS_TABLE_ID,
  MESSAGES_TABLE_ID,
  DRIVERS_TABLE_ID,
  REQUESTS_TABLE_ID,
  VEHICLES_TABLE_ID,
  REGISTRATION_STAGE_TABLE_ID,
  ADMIN_ACTIVITY_LOG_TABLE_ID,
  VEHICLE_TYPES_TABLE_ID,
  DELIVERY_SETTINGS_TABLE_ID,
  DELIVERY_RATINGS_TABLE_ID,
  PROMOTIONS_TABLE_ID,
  STORE_APPLICATIONS_TABLE_ID,
};
